// Datetime cannot build a date in a named zone, and these boundaries must be Stockholm days whatever
// the browser's zone. @internationalized/date, the date pickers' own library, does that; Datetime
// still serialises every instant the API receives.
import { parseAbsolute, parseAbsoluteToLocal, parseDate, toCalendarDate, today, toZoned } from '@internationalized/date'
import { SETTLEMENT_TIME_ZONE } from 'api/settlementExports/settlementExports.constants'
import type {
  CreateSettlementExportBody,
  SettlementExportListWindow,
  SettlementExportRecord,
  SettlementExportStatus,
} from 'api/settlementExports/settlementExports.types'
import type { TFunction } from 'i18next'
import type { StatusIconVariants } from 'ui/atoms/StatusIcon/StatusIcon'
import Datetime from 'utils/datetime/datetime'
import type { DayRange } from './SettlementExportButton.types'

// conventions-allow: epoch-date-arithmetic — mirrors the server's limit checks, which count whole 24-hour spans, not calendar days
const DAY_MS = 86_400_000

type InstantPeriod = { startTime: ISODateTime; endTime: ISODateTime }

/**
 * The instants the API wants for a range of whole Stockholm days: 00:00 on the first day, and 00:00
 * on the day *after* the last day as the exclusive end. Ending at 23:00 or 23:59 of the last day is
 * accepted by the server and silently drops the final hour of the export.
 */
export function dayRangeToPeriod({ firstDay, lastDay }: DayRange): InstantPeriod {
  const start = toZoned(parseDate(firstDay), SETTLEMENT_TIME_ZONE).toDate()
  const end = toZoned(parseDate(lastDay).add({ days: 1 }), SETTLEMENT_TIME_ZONE).toDate()
  return { startTime: Datetime.dateToISODateTime(start), endTime: Datetime.dateToISODateTime(end) }
}

export function dayRangeToCreateBody(systemId: number, range: DayRange): CreateSettlementExportBody {
  const { startTime, endTime } = dayRangeToPeriod(range)
  return { system: systemId, start_time: startTime, end_time: endTime }
}

export function dayRangeToListWindow(range: DayRange): SettlementExportListWindow {
  const { startTime, endTime } = dayRangeToPeriod(range)
  return { createdAfter: startTime, createdBefore: endTime }
}

/** Not always 24 × days: a range containing a DST change has one hour more or less. */
export function dayRangeHours(range: DayRange): number {
  const { startTime, endTime } = dayRangeToPeriod(range)
  return (Date.parse(endTime) - Date.parse(startTime)) / 3_600_000
}

/**
 * Mirrors both of the server's limits — the 32-day export period and the 30-day list window — which
 * floor the span to whole 24-hour days before comparing, so a range across the spring DST change may
 * hold one calendar day more.
 */
export function dayRangeExceedsDays(range: DayRange, maxDays: number): boolean {
  const { startTime, endTime } = dayRangeToPeriod(range)
  return Math.floor((Date.parse(endTime) - Date.parse(startTime)) / DAY_MS) > maxDays
}

/** Date pickers select in the browser's zone, so the day they showed is the browser-local one. */
function toPickerDay(instant: ISODateTime): string {
  return toCalendarDate(parseAbsoluteToLocal(Datetime.toISOString(instant))).toString()
}

export function pickerSelectionToDayRange(
  startTime: ISODateTime | null | undefined,
  endTime: ISODateTime | null | undefined
): DayRange | null {
  if (!startTime || !endTime) return null
  const range = { firstDay: toPickerDay(startTime), lastDay: toPickerDay(endTime) }
  return range.firstDay <= range.lastDay ? range : null
}

/**
 * The latest instant either picker offers: the end of today in Stockholm. A later export period has
 * no settlement results yet, and a later request window cannot hold any export.
 */
export function endOfSettlementToday(): ISODateTime {
  const endOfToday = toZoned(today(SETTLEMENT_TIME_ZONE).add({ days: 1 }), SETTLEMENT_TIME_ZONE).subtract({
    seconds: 1,
  })
  return Datetime.dateToISODateTime(endOfToday.toDate())
}

/** Whether an export requested at `instant` is listed by a request window of `range`. */
export function isRequestedInDayRange(range: DayRange, instant: string): boolean {
  const { startTime, endTime } = dayRangeToPeriod(range)
  const requestedAt = Date.parse(instant)
  return Date.parse(startTime) <= requestedAt && requestedAt < Date.parse(endTime)
}

export function recentDayRange(days: number): DayRange {
  const lastDay = today(SETTLEMENT_TIME_ZONE)
  return { firstDay: lastDay.subtract({ days: days - 1 }).toString(), lastDay: lastDay.toString() }
}

/** An export's period as the Stockholm days it covers; the API's `end_time` is exclusive. */
export function exportPeriodToDayRange(startTime: string, endTime: string): DayRange {
  return {
    firstDay: toCalendarDate(parseAbsolute(startTime, SETTLEMENT_TIME_ZONE)).toString(),
    lastDay: toCalendarDate(parseAbsolute(endTime, SETTLEMENT_TIME_ZONE)).subtract({ days: 1 }).toString(),
  }
}

export const SETTLEMENT_EXPORT_STATUS_ICONS: Record<SettlementExportStatus, StatusIconVariants> = {
  Created: 'queued',
  Processing: 'spinner',
  Finished: 'generic_finished',
  Failed: 'generic_failed',
}

export function getSettlementExportStatusDetail(settlementExport: SettlementExportRecord, t: TFunction): string {
  switch (settlementExport.status) {
    case 'Processing':
      return settlementExport.started_at
        ? t(`Started {{time}}`, {
            time: Datetime.toLocalTime(Datetime.toISOString(settlementExport.started_at), 'hour'),
          })
        : ''
    case 'Failed':
      // The API deliberately carries no failure reason; support reads it in the Django admin.
      return t(`Export failed — contact support.`)
    case 'Finished':
      return settlementExport.file_name
    case 'Created':
      return ''
  }
}
